import React, { useState, useEffect, memo } from "react";
import {
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import { useNavigate, useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import GoogleAutoCompleteField from "../../../../globals/components/forms/GoogleAutoCompleteField";
import { normalizeUsState, isAllowedUsState } from "../../../../globals/utils/usStates";
import {
  createEmptyLocation,
  hasLocationAddressErrors,
  mapLocationFormToApiPayload,
  validateLocationAddressFields,
} from "./lawFirmLocationFormConfig";

const customStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    backgroundColor: "#f8f8f8",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 10px", // Match padding with MUI TextField
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300, // Increase z-index to overlay other elements
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgba(0, 0, 0, 0.6)", // Similar color as MUI placeholder
    fontSize: "16px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.87)", // Match MUI font color
  }),
};

const DropdownWithCreatableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  error = false,
}) => (
  <Box sx={{ ".MuiOutlinedInput-root": { padding: "0 10px" } }}>
    <CreatableSelect
      isClearable
      options={options}
      components={{ MenuList }}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      styles={{
        ...customStyles,
        control: (provided) => ({
          ...customStyles.control(provided),
          ...(error ? { border: "1px solid #d32f2f", "&:hover": { border: "1px solid #d32f2f" } } : {}),
        }),
      }}
    />
  </Box>
);

// Virtualized MenuList component for dropdowns
const MenuList = memo((props) => {
  const height = 35;
  const { options, children, maxHeight, getValue } = props;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
});

function CreateLocations() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([createEmptyLocation()]);

  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCounties, setAllCounties] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fuzzyMatchLocationDialogOpen, setFuzzyMatchLocationDialogOpen] = useState(false);
  const [fuzzyMatchLocationResult, setFuzzyMatchLocationResult] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [addressFieldErrors, setAddressFieldErrors] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        const counties = await apiClient(`/fields/counties/`, "GET");
        setAllCounties(counties);
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadData();
  }, []);

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...locations];
    updatedLocations[index][field] = value;
    setLocations(updatedLocations);
    setAddressFieldErrors((prev) => {
      const next = prev.slice();
      while (next.length <= index) next.push({});
      next[index] = { ...(next[index] || {}), [field]: false };
      return next;
    });
  };

  const addLocation = () => {
    setLocations((prev) => [...prev, createEmptyLocation(`Location ${prev.length + 1}`)]);
  };

  const removeLocation = (index) => {
    const updatedLocations = [...locations];
    updatedLocations.splice(index, 1);
    setLocations(updatedLocations);
  };

  const handleGoogleCallback = (
    index,
    address,
    city,
    state,
    county,
    zipCode
  ) => {
    handleLocationChange(index, "address", address);
    handleLocationChange(index, "city", city);
    handleLocationChange(index, "state", normalizeUsState(state, allStates));
    handleLocationChange(index, "county", county);
    handleLocationChange(index, "zipCode", zipCode);
  };

  const handleZipCodeChange = async (index, zipCode) => {
    handleLocationChange(index, "zipCode", zipCode);
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state, county } = response;
        handleLocationChange(index, "city", city);
        handleLocationChange(index, "state", normalizeUsState(state, allStates));
        handleLocationChange(index, "county", county);
      } catch (error) {
        console.error("Error fetching city/state/county by zip code:", error);
      }
    }
  };

  const handleCreateLocation = async () => {
    setSubmitting(true);

    const errors = locations.map((loc) => validateLocationAddressFields(loc));
    const hasErrors = errors.some((e) => hasLocationAddressErrors(e));
    if (hasErrors) {
      setAddressFieldErrors(errors);
      setSubmitting(false);
      toast.error("Please fill in all required address fields: Address, Zip Code, City, State, and County.");
      return;
    }
    setAddressFieldErrors([]);

    // Normalize/validate states so abbreviations like "FL" don't create new states.
    const normalizedLocations = locations.map((location) => {
      const normalizedState = normalizeUsState(location.state, allStates);
      return { ...location, state: normalizedState };
    });

    const invalidState = normalizedLocations.find(
      (loc) => loc.state && !isAllowedUsState(loc.state, allStates)
    );
    if (invalidState) {
      toast.error(
        `Invalid state "${invalidState.state}". Please select a valid state.`
      );
      setSubmitting(false);
      return;
    }

    const dataToSend = normalizedLocations.map((location) => mapLocationFormToApiPayload(location, id));

    // Fuzzy match: check each location for existing similar address
    for (let i = 0; i < dataToSend.length; i++) {
      const loc = dataToSend[i];
      try {
        const fuzzyRes = await apiClient("/fuzzy-match-lawfirm-location", "POST", {
          lawfirm_id: Number(id),
          address: loc.address,
          city: loc.city,
          state: loc.state,
          zip_code: loc.zip_code,
          county: loc.county || "",
          country: "USA",
        });
        const matchedId = fuzzyRes?.match?.law_firm_location_id;
        if (fuzzyRes?.match != null && matchedId != null) {
          setPendingSubmitData(dataToSend);
          setFuzzyMatchLocationResult({
            locationIndex: i,
            law_firm_location_id: matchedId,
            score: fuzzyRes?.score ?? 0,
            addressSummary: [loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", "),
          });
          setFuzzyMatchLocationDialogOpen(true);
          setSubmitting(false);
          return;
        }
      } catch (err) {
        console.error("Fuzzy match check failed for location:", err);
      }
    }

    try {
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);
      navigate(-1);
      toast.success("Locations created successfully!");
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseExistingLocation = async () => {
    if (!pendingSubmitData || fuzzyMatchLocationResult == null) return;
    const index = fuzzyMatchLocationResult.locationIndex;
    const filtered = pendingSubmitData.filter((_, i) => i !== index);
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingSubmitData(null);
    if (filtered.length === 0) {
      toast.info("Skipped creating duplicate location.");
      setSubmitting(false);
      navigate(-1);
      return;
    }
    try {
      setSubmitting(true);
      await apiClient(`/lawfirm-location/`, "POST", filtered);
      navigate(-1);
      toast.success("Locations created successfully!");
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateNewLocationAnyway = async () => {
    if (!pendingSubmitData) return;
    const dataToSend = pendingSubmitData;
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingSubmitData(null);
    try {
      setSubmitting(true);
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);
      navigate(-1);
      toast.success("Locations created successfully!");
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography
            variant="h4"
            gutterBottom
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            Create Locations
          </Typography>
          {loadingCitiesStates ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : (
            locations.map((location, index) => (
              <Box sx={{ marginY: 2, border: "1px solid #e0e0e0", borderRadius: "8px", padding: 2 }}>
                <Grid container spacing={2}>
                  {/* Location Name */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Location Name"
                      fullWidth
                      variant="outlined"
                      value={location.name}
                      onChange={(e) =>
                        handleLocationChange(index, "name", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Address Search Field */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <FormLabel>Search Address</FormLabel>
                      <GoogleAutoCompleteField
                        placeholder="Search address"
                        includeCounty
                        searchIconPosition="end"
                        callback={(address, city, state, county, zipCode) =>
                          handleGoogleCallback(
                            index,
                            address,
                            city,
                            state,
                            county,
                            zipCode
                          )
                        }
                      />
                    </FormControl>
                  </Grid>

                  {/* Manual Address Field */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Address"
                      fullWidth
                      variant="outlined"
                      value={location.address}
                      onChange={(e) =>
                        handleLocationChange(index, "address", e.target.value)
                      }
                      error={!!addressFieldErrors[index]?.address}
                      helperText={addressFieldErrors[index]?.address ? "Required" : ""}
                    />
                  </Grid>

                  {/* Zip Code */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Zip Code"
                      fullWidth
                      variant="outlined"
                      value={location.zipCode}
                      onChange={(e) =>
                        handleZipCodeChange(index, e.target.value)
                      }
                      error={!!addressFieldErrors[index]?.zipCode}
                      helperText={addressFieldErrors[index]?.zipCode ? "Required" : ""}
                    />
                  </Grid>

                  {/* City */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={!!addressFieldErrors[index]?.city}>
                      <FormLabel>City</FormLabel>
                      <DropdownWithCreatableSelect
                        options={allCities.map((city) => ({
                          label: city,
                          value: city,
                        }))}
                        value={
                          location.city
                            ? { label: location.city, value: location.city }
                            : null
                        }
                        onChange={(opt) =>
                          handleLocationChange(
                            index,
                            "city",
                            opt ? opt.value : ""
                          )
                        }
                        placeholder="Select or type a city"
                        error={!!addressFieldErrors[index]?.city}
                      />
                      {addressFieldErrors[index]?.city && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>Required</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* State */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={!!addressFieldErrors[index]?.state}>
                      <FormLabel>State</FormLabel>
                      <DropdownWithCreatableSelect
                        options={allStates.map((state) => ({
                          label: state,
                          value: state,
                        }))}
                        value={
                          location.state
                            ? { label: location.state, value: location.state }
                            : null
                        }
                        onChange={(opt) => {
                          const next = normalizeUsState(opt ? opt.value : "", allStates);
                          handleLocationChange(index, "state", next);
                        }}
                        placeholder="Select or type a state"
                        error={!!addressFieldErrors[index]?.state}
                      />
                      {addressFieldErrors[index]?.state && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>Required</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* County */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={!!addressFieldErrors[index]?.county}>
                      <FormLabel>County</FormLabel>
                      <DropdownWithCreatableSelect
                        options={allCounties.map((county) => ({
                          label: county,
                          value: county,
                        }))}
                        value={
                          location.county
                            ? { label: location.county, value: location.county }
                            : null
                        }
                        onChange={(opt) =>
                          handleLocationChange(
                            index,
                            "county",
                            opt ? opt.value : ""
                          )
                        }
                        placeholder="Select or type a county"
                        error={!!addressFieldErrors[index]?.county}
                      />
                      {addressFieldErrors[index]?.county && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>Required</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Phone */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Phone"
                      fullWidth
                      variant="outlined"
                      value={location.phone}
                      onChange={(e) =>
                        handleLocationChange(index, "phone", e.target.value)
                      }
                      slotProps={{ input: {
                        inputComponent: InputMask,
                        inputProps: {
                          mask: "+9 999-999-9999",
                          maskChar: "_",
                        },
                      }}}
                    />
                  </Grid>

                  {/* Fax */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Fax"
                      fullWidth
                      variant="outlined"
                      value={location.fax}
                      onChange={(e) =>
                        handleLocationChange(index, "fax", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Customer Number */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Customer Number"
                      fullWidth
                      variant="outlined"
                      value={location.customerNumber || ""}
                      onChange={(e) =>
                        handleLocationChange(
                          index,
                          "customerNumber",
                          e.target.value
                        )
                      }
                    />
                  </Grid>

                  {/* Use Separate Accounting Contact */}
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={location.useSeparateAccounting}
                          onChange={() =>
                            handleLocationChange(
                              index,
                              "useSeparateAccounting",
                              !location.useSeparateAccounting
                            )
                          }
                        />
                      }
                      label="Use separate accounting contact for this location"
                    />
                  </Grid>

                  {/* Accounting Contact Fields (Conditional) */}
                  {location.useSeparateAccounting && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Accounting Contact Name"
                          fullWidth
                          variant="outlined"
                          value={location.accountingContact.name}
                          onChange={(e) =>
                            handleLocationChange(index, "accountingContact", {
                              ...location.accountingContact,
                              name: e.target.value,
                            })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Accounting Contact Email"
                          fullWidth
                          variant="outlined"
                          value={location.accountingContact.email}
                          onChange={(e) =>
                            handleLocationChange(index, "accountingContact", {
                              ...location.accountingContact,
                              email: e.target.value,
                            })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Accounting Contact Phone"
                          fullWidth
                          variant="outlined"
                          value={location.accountingContact.phone}
                          onChange={(e) =>
                            handleLocationChange(index, "accountingContact", {
                              ...location.accountingContact,
                              phone: e.target.value,
                            })
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {/* Address Type Checkboxes */}
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={location.addressTypes.physical}
                          onChange={() =>
                            handleLocationChange(index, "addressTypes", {
                              ...location.addressTypes,
                              physical: !location.addressTypes.physical,
                            })
                          }
                        />
                      }
                      label="Physical Address"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={location.addressTypes.mailing}
                          onChange={() =>
                            handleLocationChange(index, "addressTypes", {
                              ...location.addressTypes,
                              mailing: !location.addressTypes.mailing,
                            })
                          }
                        />
                      }
                      label="Mailing Address"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={location.addressTypes.billing}
                          onChange={() =>
                            handleLocationChange(index, "addressTypes", {
                              ...location.addressTypes,
                              billing: !location.addressTypes.billing,
                            })
                          }
                        />
                      }
                      label="Billing Address"
                    />
                  </Grid>

                  {/* Remove Location Button */}
                  {locations.length > 1 && (
                    <Grid size={{ xs: 12 }} sx={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeLocation(index)}
                        sx={{ marginY: 2 }}
                      >
                        Remove Location
                      </Button>
                    </Grid>
                  )}
                </Grid>
                </Box>
            ))
          )}

          {/* Add Another Location and Submit Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "16px",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={addLocation}
              startIcon={<AddIcon />}
            >
              ADD ANOTHER LOCATION
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateLocation}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : "Create Locations"}
            </Button>
          </Box>
        </Paper>
      </Container>

      <Dialog open={fuzzyMatchLocationDialogOpen} onClose={() => { setFuzzyMatchLocationDialogOpen(false); setFuzzyMatchLocationResult(null); setPendingSubmitData(null); }}>
        <DialogTitle>Similar location found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar location already exists for this address{fuzzyMatchLocationResult?.addressSummary ? `: ${fuzzyMatchLocationResult.addressSummary}` : ""}{fuzzyMatchLocationResult?.score ? ` (match score: ${Number(fuzzyMatchLocationResult.score).toFixed(0)}%)` : ""}. Do you want to use the existing location or create this new one anyway?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLocation} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLocationAnyway} color="secondary">
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CreateLocations;
